import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import { returnBondOptionUnderlyingChanges } from "../helper/bondOptionTicketHelper";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import { useBondSecuritySearch } from "../helper/useBondSecuritySearch";
import DirectionSelect from "../ticketFields/DirectionSelect";
import NominalInput from "../ticketFields/NominalInput";
import ReferenceDataSelect, {
  IReferenceDataOption,
} from "../ticketFields/ReferenceDataSelect";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useBondOptionTicket } from "./useBondOptionTicket";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";

// The bond option fields a Trade Creation leg row shows inline: the underlying, size,
// direction and settlement. Option terms live in BondOptionDetailFields.
const BondOptionCoreFields = ({
  ticketId,
  highlightedKeys,
}: ITicketFieldsProps) => {
  const { ticket, updateBondOptionTicket, requiredKeys } =
    useBondOptionTicket(ticketId);
  const { searchIsinOptions, searchSecurityNameOptions } =
    useBondSecuritySearch();

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  // Payment currency is only a default - a quarter of real bond options pay in another.
  const handleUnderlyingSelect = (option: IReferenceDataOption | null) => {
    const isin = option?.value ?? "";
    updateBondOptionTicket(
      returnBondOptionUnderlyingChanges(findBondSecurityByIsin(isin), isin),
    );
  };

  return (
    <div
      data-testid="BondOptionCoreFieldsContainer"
      id="BondOptionCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Underlying ISIN"
        testId="BondOptionTicketIsinRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="BondOptionTicketIsinInput"
          placeholder="Search ISIN ..."
          selected={
            ticket.isin ? { label: ticket.isin, value: ticket.isin } : null
          }
          searchOptions={searchIsinOptions}
          onSelect={handleUnderlyingSelect}
        />
      </TicketRow>

      <TicketRow
        label="Underlying"
        testId="BondOptionTicketSecurityNameRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="BondOptionTicketSecurityNameInput"
          placeholder="Search security ..."
          selected={
            ticket.isin
              ? { label: ticket.securityName, value: ticket.isin }
              : null
          }
          searchOptions={searchSecurityNameOptions}
          onSelect={handleUnderlyingSelect}
        />
      </TicketRow>

      <TicketRow
        label="Nominal"
        testId="BondOptionTicketNominalRow"
        isHighlighted={isMissing("nominal")}
        isRequired={isRequired("nominal")}
      >
        <NominalInput
          testId="BondOptionTicketNominalInput"
          value={ticket.nominal}
          onCommit={(nominal) => updateBondOptionTicket({ nominal })}
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="BondOptionTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateBondOptionTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.nominal}
        splits={ticket.additionalAllocations}
        sizeKind="nominal"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateBondOptionTicket({ additionalAllocations })}
      />

      <TicketRow
        label="Direction"
        testId="BondOptionTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="BondOptionTicketDirectionInput"
          value={ticket.direction}
          onChange={(direction) => updateBondOptionTicket({ direction })}
        />
      </TicketRow>

      <TicketRow
        label="Settlement Date"
        testId="BondOptionTicketSettlementDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
      >
        <TicketDateInput
          testId="BondOptionTicketSettlementDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.BondOption, ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) =>
            updateBondOptionTicket({ settlementDate })
          }
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="BondOptionTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateBondOptionTicket({ transactionNumber })}
      />
    </div>
  );
};

export default BondOptionCoreFields;
